import { formatValue, humanizeMeasure, formatFromColumn } from "@/lib/reports/labels";

import type { ChartBuildCtx, ChartOption } from "./context";

export function buildWaterfallOption({ query, data, base, xKey, yKey }: ChartBuildCtx): ChartOption {
  // ECharts waterfall via two stacked bars: invisible placeholder + visible Δ.
  // Two supported shapes:
  //   Shape A: 1 measure × N rows — each row is a step (current default).
  //   Shape B: M measures × any rows — each measure is a step (funnel-
  //   contribution prompts like "How does each stage contribute?").
  //   When the AI gives us shape B we transpose: sum each measure
  //   across all rows and treat the totals as step deltas.
  const useMeasureAsStep = query.measures.length > 1;
  const stepNames = useMeasureAsStep
    ? query.measures.map((m) => humanizeMeasure(m))
    : data.rows.map((r) => String(r[xKey] ?? ""));
  // For shape B (measure-as-step): each step value is the delta
  // from the previous measure's total. The first step uses the
  // absolute total. This produces a funnel-shaped waterfall:
  // first bar is "Floats sent: +12000", next is "Opens: -7000",
  // etc.
  const values = useMeasureAsStep
    ? query.measures.map((m, i) => {
        const total = data.rows.reduce((acc, r) => acc + Number(r[m] ?? 0), 0);
        if (i === 0) return total;
        const prevTotal = data.rows.reduce(
          (acc, r) => acc + Number(r[query.measures[i - 1]] ?? 0),
          0,
        );
        return total - prevTotal;
      })
    : data.rows.map((r) => Number(r[yKey] ?? 0));
  const placeholders: number[] = [];
  let running = 0;
  for (const v of values) {
    placeholders.push(v >= 0 ? running : running + v);
    running += v;
  }
  const yFmt = useMeasureAsStep
    ? formatFromColumn(query.measures[0], data.columns)
    : formatFromColumn(yKey, data.columns);
  return {
    ...base,
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (params: Array<{ name: string; value: number; seriesName: string; dataIndex: number }>) => {
        const delta = params.find((p) => p.seriesName === "Δ");
        if (!delta) return "";
        const actual = values[delta.dataIndex];
        const sign = actual >= 0 ? "+" : "−";
        return `${delta.name}<br/>${sign} ${formatValue(Math.abs(actual), yFmt)}`;
      },
    },
    xAxis: {
      type: "category",
      data: stepNames,
      axisLabel: {
        color: "#6b7280",
        fontSize: 11,
        interval: 0,
        rotate: 30,
        formatter: (v: string) => (typeof v === "string" && v.length > 14 ? v.slice(0, 13) + "…" : v),
      },
    },
    yAxis: {
      type: "value",
      axisLabel: { color: "#6b7280", fontSize: 11, formatter: (v: number) => formatValue(v, yFmt) },
    },
    series: [
      {
        name: "spacer",
        type: "bar",
        stack: "wf",
        itemStyle: { color: "transparent" },
        emphasis: { itemStyle: { color: "transparent" } },
        data: placeholders,
      },
      {
        name: "Δ",
        type: "bar",
        stack: "wf",
        itemStyle: {
          color: (p: { dataIndex: number }) => (values[p.dataIndex] >= 0 ? "#10b981" : "#dc2626"),
          // Per-data-item borderRadius: top-rounded for positive
          // deltas (growth ↑), bottom-rounded for negative (loss ↓)
          // so the bar shape echoes the polarity.
          borderRadius: ([4, 4, 0, 0] as const),
        },
        data: values.map((v, i) => ({
          value: Math.abs(v),
          itemStyle: {
            color: v >= 0 ? "#10b981" : "#dc2626",
            borderRadius: v >= 0 ? [4, 4, 0, 0] : [0, 0, 4, 4],
          },
          // Carry the original signed value through for the label.
          _signed: v,
          _idx: i,
        })),
        label: {
          show: true,
          position: "top",
          color: "#374151",
          fontSize: 10,
          formatter: (p: { dataIndex: number }) => {
            const v = values[p.dataIndex];
            const sign = v >= 0 ? "+" : "−";
            // Format per-measure: percent rates stay percent; counts
            // round to int (a waterfall of "+12.7 floats" is silly).
            if (yFmt === "percent" || yFmt === "currency" || yFmt === "duration_minutes") {
              return `${sign}${formatValue(Math.abs(v), yFmt)}`;
            }
            return `${sign}${formatValue(Math.round(Math.abs(v)), "number")}`;
          },
        },
      },
    ],
  } as ChartOption;
}
